// Follow-up Statuses Master — AdminCatalogue sub-tab.
// Mirrors `LeadSourcesTab.jsx`. Adds Color + Display Order columns. Admin/MD manage;
// Manager/Marketing Head read-only; BDE blocked at page level.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, MagnifyingGlass,
  X, Check, Warning, Lock,
} from "@phosphor-icons/react";
import { STATUS_COLOR_MAP, statusBadgeCls } from "../leads/FollowupStatusSelect";

const inputCls = "rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 text-sm outline-none";

// 15 default slugs — kept in sync with backend `_DEFAULT_FOLLOWUP_STATUS_SLUGS`.
const DEFAULT_SLUGS = new Set([
  "not_contacted","call_scheduled","follow_up_pending","interested",
  "quotation_required","site_visit_required","demo_required",
  "not_interested","no_response","converted","lost","transferred",
  "completed","rescheduled","missed",
]);

const COLOR_CHOICES = Object.keys(STATUS_COLOR_MAP);

const FollowupStatusesTab = ({ user }) => {
  const role = (user?.role || "").toLowerCase();
  const canManage = ["admin", "md"].includes(role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/follow-up-statuses`, {
        params: { include_inactive: showInactive, with_usage: true },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load statuses");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.slug || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const setActive = async (row, next) => {
    if (!canManage) return;
    try {
      const path = next ? "activate" : "deactivate";
      await axios.patch(`${API}/leads/follow-up-statuses/${row.slug}/${path}`, { requester_role: user.role });
      toast.success(`${row.name} ${next ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (row) => {
    if (!canManage) return;
    if (!window.confirm(`Delete status "${row.name}"? Only possible when no follow-up uses it.`)) return;
    try {
      await axios.delete(`${API}/leads/follow-up-statuses/${row.slug}`, { params: { requester_role: user.role } });
      toast.success(`Deleted "${row.name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="adm-followup-statuses">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search status name, slug, description…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white"
            data-testid="fs-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 accent-[#002FA7]"
            data-testid="fs-show-inactive"
          />
          Show inactive + legacy
        </label>
        {canManage && (
          <button
            onClick={() => setEditor({ mode: "create", data: { name: "", description: "", color: "slate", display_order: 0, is_active: true } })}
            className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2"
            data-testid="fs-new"
          >
            <Plus size={14} weight="bold" /> Add Status
          </button>
        )}
        {!canManage && (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 px-2 py-1">
            <Lock size={11} /> View-only — admin/MD can manage
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><div className="loading-spinner" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="fs-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="p-3">Status Name</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Description</th>
                <th className="p-3 text-center">Color</th>
                <th className="p-3 text-right">Order</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Usage</th>
                <th className="p-3">Kind</th>
                <th className="p-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="p-10 text-center text-slate-500">No statuses match this search.</td></tr>
              ) : filtered.map((r) => {
                const isLegacy = !!r.is_legacy;
                const isDefault = DEFAULT_SLUGS.has(r.slug);
                const usage = r.usage_count ?? 0;
                const deleteBlocked = isDefault || usage > 0 || isLegacy;
                const deleteTooltip = isLegacy
                  ? "Legacy value (auto-detected)"
                  : isDefault
                  ? "Default master — protected"
                  : usage > 0
                  ? `In use by ${usage} follow-up(s) — deactivate instead`
                  : "Delete";
                return (
                  <tr key={r.slug + (isLegacy ? "_l" : "")} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fs-row-${r.slug}`}>
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3 text-xs font-mono text-slate-600">{r.slug}</td>
                    <td className="p-3 text-xs text-slate-600 max-w-[240px] truncate" title={r.description}>{r.description || "—"}</td>
                    <td className="p-3 text-center">
                      <span className={`${statusBadgeCls(r.color)} text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold`}>{r.color || "slate"}</span>
                    </td>
                    <td className="p-3 text-right tabular-nums text-xs text-slate-600">{r.display_order ?? 0}</td>
                    <td className="p-3 text-center">
                      {isLegacy ? (
                        <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 font-bold">N/A</span>
                      ) : (
                        <button
                          onClick={() => setActive(r, !r.is_active)}
                          disabled={!canManage}
                          className="inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={r.is_active ? "Deactivate" : "Activate"}
                          data-testid={`fs-toggle-${r.slug}`}
                        >
                          {r.is_active
                            ? <ToggleRight size={24} weight="fill" className="text-emerald-500" />
                            : <ToggleLeft size={24} weight="fill" className="text-slate-400" />}
                        </button>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums" data-testid={`fs-usage-${r.slug}`}>{usage}</td>
                    <td className="p-3">
                      {isLegacy ? (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 font-bold">Legacy</span>
                      ) : isDefault ? (
                        <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-0.5 font-bold">Default</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 font-bold">Master</span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {!isLegacy && canManage && (
                        <button
                          onClick={() => setEditor({ mode: "edit", data: r })}
                          className="p-1.5 hover:bg-slate-100 text-[#002FA7]"
                          title="Edit"
                          data-testid={`fs-edit-${r.slug}`}
                        ><PencilSimple size={14} /></button>
                      )}
                      {!isLegacy && canManage && (
                        <button
                          onClick={() => !deleteBlocked && remove(r)}
                          disabled={deleteBlocked}
                          className={`p-1.5 ${deleteBlocked ? "text-slate-300 cursor-not-allowed" : "hover:bg-red-50 text-red-600"}`}
                          title={deleteTooltip}
                          data-testid={`fs-delete-${r.slug}`}
                        ><Trash size={14} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <StatusEditor
          editor={editor}
          user={user}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </div>
  );
};

const StatusEditor = ({ editor, user, onClose, onSaved }) => {
  const isEdit = editor.mode === "edit";
  const [name, setName] = useState(editor.data?.name || "");
  const [description, setDescription] = useState(editor.data?.description || "");
  const [color, setColor] = useState(editor.data?.color || "slate");
  const [displayOrder, setDisplayOrder] = useState(editor.data?.display_order ?? 0);
  const [isActive, setIsActive] = useState(editor.data?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!name.trim()) { setErr("Status name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/leads/follow-up-statuses/${editor.data.slug}`, {
          name, description, color, display_order: Number(displayOrder) || 0, is_active: isActive, requester_role: user.role,
        });
      } else {
        await axios.post(`${API}/leads/follow-up-statuses`, {
          name, description, color, display_order: Number(displayOrder) || 0, is_active: isActive,
          requester_user_id: user.user_id, requester_name: user.name, requester_role: user.role,
        });
      }
      toast.success(isEdit ? `Updated "${name}"` : `Created "${name}"`);
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-white shadow-2xl max-w-lg w-full my-12" onClick={(e) => e.stopPropagation()} data-testid="fs-editor">
        <div className="border-b border-slate-200 px-5 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold">{isEdit ? "Edit" : "New"} Follow-up Status</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && (
            <div className="flex items-start gap-2 border border-rose-300 bg-rose-50 text-rose-800 text-sm p-3">
              <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Status Name *</label>
            <input data-testid="fs-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls + " mt-1"} autoFocus />
            {isEdit && (
              <div className="text-[10px] text-slate-500 mt-1">Slug (auto, immutable): <span className="font-mono">{editor.data.slug}</span></div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Description</label>
            <textarea data-testid="fs-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputCls + " mt-1"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Display Order</label>
              <input type="number" data-testid="fs-order" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} className={inputCls + " mt-1"} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Color</label>
              <div className="mt-1 flex flex-wrap gap-1">
                {COLOR_CHOICES.map((c) => (
                  <button key={c} type="button" onClick={() => setColor(c)}
                    data-testid={`fs-color-${c}`}
                    className={`${statusBadgeCls(c)} px-2 py-1 text-[10px] font-bold uppercase tracking-wider border-2 ${color === c ? "border-slate-900" : "border-transparent"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="fs-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4 accent-[#002FA7]" />
            Active (visible in form dropdowns)
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="fs-save"
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300 flex items-center gap-2">
            <Check size={12} weight="bold" /> {saving ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowupStatusesTab;
